import React, { useEffect, useState } from "react";
import { Zap, TrendingUp, Loader2, Star } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";

const Delta = ({ during, before, label }) => {
  const lift = before > 0 ? Math.round(((during - before) / before) * 100) : during > 0 ? 100 : 0;
  const up = lift > 0;
  return (
    <div className="flex-1 min-w-[92px]">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="text-lg font-extrabold text-foreground leading-tight">{during.toLocaleString()}</p>
      <p className={`text-[11px] font-semibold ${up ? "text-[#16a34a]" : lift < 0 ? "text-orange-500" : "text-muted-foreground"}`}>
        {up ? "+" : ""}{lift}% · was {before.toLocaleString()}
      </p>
    </div>
  );
};

export const BoostReport = () => {
  const { t } = useLang();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/my/boosts").then((r) => r.data || []).catch(() => []),
      api.get("/my/promotions").then((r) => r.data || []).catch(() => []),
    ]).then(([boosts, featured]) =>
      setRows([...boosts, ...featured].sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1))));
  }, []);

  if (rows === null) return <div className="h-28 rounded-2xl bg-secondary animate-pulse flex items-center justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>;
  if (!rows.length) return null;

  const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="boost-report">
      <h3 className="font-bold text-foreground flex items-center gap-2 mb-1">
        <Zap size={17} className="text-[#b8860b]" /> {t("bst.reportTitle")}
      </h3>
      <p className="text-xs text-muted-foreground mb-4">{t("bst.reportSub")}</p>
      <div className="space-y-3">
        {rows.map((b) => (
          <div key={b.id} className="rounded-xl border border-border p-3" data-testid={`boost-row-${b.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
              <div>
                <p className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                  {b.kind === "featured" ? <Star size={13} className="fill-[#f5b942] text-[#f5b942] shrink-0" /> : <Zap size={13} className="text-[#b8860b] shrink-0" />}
                  {b.dealTitle}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {fmt(b.startedAt)} – {fmt(b.until)}{b.city ? ` · ${b.city}` : ""}
                  {b.kind === "featured" ? ` · ${t("bst.kindFeatured", { km: b.reachKm })}` : ""}
                  {b.kind === "reach" ? ` · ${t("bst.kindReach", { km: b.reachKm })}` : ""}
                  {b.kind === "exclusive" ? ` · ${t("bst.kindExclusive")}` : ""}
                </p>
              </div>
              <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full ${b.live ? "bg-[#fdf6e3] dark:bg-[#3a2f10] text-[#b8860b]" : "bg-secondary text-muted-foreground"}`}>
                {b.live ? t("bst.running") : t("bst.finished")}
              </span>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Delta during={b.during.views} before={b.before.views} label={t("fn.views")} />
              <Delta during={b.during.grabs} before={b.before.grabs} label={t("fn.grabs")} />
              <Delta during={b.during.directions} before={b.before.directions} label={t("bst.directionsShort")} />
            </div>
            {b.topSpotShare != null && (
              <div className="mt-2.5 pt-2.5 border-t border-border/60" data-testid={`boost-share-${b.id}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full rounded-full ${b.crowded ? "bg-[#f5b942]" : "bg-[#16a34a]"}`}
                      style={{ width: `${Math.round(b.topSpotShare * 100)}%` }} />
                  </div>
                  <span className="text-[11px] font-bold text-muted-foreground shrink-0">{Math.round(b.topSpotShare * 100)}%</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  {b.crowded
                    ? t("bst.shareCrowded", { n: b.overlapping, pct: Math.round(b.topSpotShare * 100) })
                    : t("bst.shareClear")}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1.5">
        <TrendingUp size={13} className="mt-0.5 shrink-0" /> {t("bst.reportNote")}
      </p>
    </div>
  );
};
